import PermissionAwareExportButton from "#src/batteries-included-components/Buttons/PermissionAwareExportButton/PermissionAwareExportButton";
import { type UserActivityLogFiltersType } from "#src/batteries-included-components/FilterAreas/UsersFilterAreas/UsersFilterAreas";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import {
  useExportActivityLogs,
  useListActivityLogs,
} from "#src/components/hooks/adapters/useActivityLogs";
import { useListRoles } from "#src/components/hooks/adapters/useRoles";
import { useListUsers } from "#src/components/hooks/adapters/useUsers";
import { useTableSortingAndPagination } from "#src/components/Redux/reducers/tableStateReducer";
import { useHasPermission } from "#src/contexts/AuthenticatedContext.helpers";
import { useStickyState } from "#src/hooks/useStickyState";
import { linkToRoleDetailPage } from "#src/routes/settings/roles-and-permissions/roles/[roleId]/detail";
import {
  Button,
  DataTable,
  DataTablePanel,
  type HeaderType,
  Icon,
  Pill,
  StorageKeys,
} from "@validereinc/common-components";
import {
  ActivityAction,
  ActivityAdapter,
  ActivityExportResource,
  ActivityResource,
  ActivitySchema,
  type ActivityType,
  RoleType,
  SortDirection,
  UserTypeEnum,
  type UserWithGroupsType,
} from "@validereinc/domain";
import { isEmpty } from "es-toolkit/compat";
import React, { useMemo, useState } from "react";

const activityModelKeys = ActivitySchema.keyof().Enum;

type UserRoleActivityLogBeforeAfter = { user_id: string };

export const UserRolesActivityLogTablePanel = ({
  viewConfigStorageKey,
  tableStateStorageKey,
}: Pick<StorageKeys, "viewConfigStorageKey" | "tableStateStorageKey">) => {
  const [viewFilters] = useStickyState<UserActivityLogFiltersType>(
    {} as UserActivityLogFiltersType,
    viewConfigStorageKey
  );

  const { resource_id, logged_by, logged_at } = viewFilters;

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "timestamp",
      sortDirection: SortDirection.DESCENDING,
      pageSize: 25,
    },
    viewFilters,
    tableStateStorageKey
  );
  const [selectedActivities, setSelectedActivities] = useState<
    Record<string, ActivityType>
  >({});
  const [canExportPermission] = useHasPermission({
    $and: ["reports:read", "reports:write"],
  });

  const viewFiltersToApply = {
    ...(resource_id && { resource_id }),
    ...(logged_by && ({ author_id: logged_by } as any)),
    ...(logged_at && ({ timestamp: logged_at } as any)),
  };

  const activitiesQueryPayload = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: {
      ...viewFiltersToApply,
      action: [ActivityAction.USER_ADD, ActivityAction.USER_REMOVE],
    },
    meta: { resourceType: ActivityResource.ROLE },
  } satisfies Parameters<typeof ActivityAdapter.getList>[0];

  const { data: activitiesData, isLoading: areActivityLogsLoading } =
    useListActivityLogs(activitiesQueryPayload);

  const activities = activitiesData?.data;

  // We cannot use the BE to filter on user IDs nested in before/after
  const filteredActivities = useMemo(() => {
    if (!viewFilters.user_id || !activities) {
      return activities ?? [];
    }

    return activities.filter((activity) => {
      const beforeUserId = (activity.before as UserRoleActivityLogBeforeAfter)
        ?.user_id;
      const afterUserId = (activity.after as UserRoleActivityLogBeforeAfter)
        ?.user_id;
      return (
        viewFilters.user_id?.includes(beforeUserId) ||
        viewFilters.user_id?.includes(afterUserId)
      );
    });
  }, [activities, viewFilters.user_id]);

  const processActivityPermissions = useMemo(() => {
    const uniqueUserIds = new Set<string>();
    const uniqueRoleIds = new Set<string>();

    activities?.forEach((activity) => {
      uniqueUserIds.add(activity.author_id);
      const beforeUserId = (activity.before as UserRoleActivityLogBeforeAfter)
        ?.user_id;
      const afterUserId = (activity.after as UserRoleActivityLogBeforeAfter)
        ?.user_id;

      if (beforeUserId) uniqueUserIds.add(beforeUserId);
      if (afterUserId) uniqueUserIds.add(afterUserId);

      uniqueRoleIds.add(activity.resource_id);
    });

    const uniqueUserIdsArray = Array.from(uniqueUserIds);
    const uniqueRoleIdsArray = Array.from(uniqueRoleIds);

    return {
      uniqueUserIdsArray,
      uniqueRoleIdsArray,
    };
  }, [activities]);

  const { uniqueUserIdsArray, uniqueRoleIdsArray } = processActivityPermissions;

  const { data: usersQueryData, isLoading: isLoadingUsers } = useListUsers(
    {
      filters: {
        id: uniqueUserIdsArray,
        type: [UserTypeEnum.human, UserTypeEnum.machine],
      },
    },
    {
      enabled: Boolean(uniqueUserIdsArray.length),
    }
  );
  const users = usersQueryData?.data ?? [];
  const userIdsToUserDetailsMap = useMemo(() => {
    return Object.fromEntries(users.map((user) => [user.id, user])) as Record<
      string,
      UserWithGroupsType
    >;
  }, [users]);

  const { data: rolesQueryData, isLoading: isLoadingRoles } = useListRoles(
    { filters: { id: uniqueRoleIdsArray } },
    {
      enabled: Boolean(uniqueRoleIdsArray.length),
    }
  );
  const roles = rolesQueryData?.data ?? [];
  const roleIdsToRoleDetailsMap = useMemo(() => {
    return Object.fromEntries(roles.map((role) => [role.id, role])) as Record<
      string,
      RoleType
    >;
  }, [roles]);

  const exportActivityLogPayload = {
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: {
      ...(!isEmpty(selectedActivities)
        ? { id: Object.keys(selectedActivities) }
        : viewFilters.user_id
          ? {
              id: filteredActivities.map(
                (filteredActivity) => filteredActivity.id
              ),
            }
          : {}),
      ...viewFiltersToApply,
    },
    meta: { resourceType: ActivityExportResource.USER, period: "" },
  } satisfies Parameters<typeof ActivityAdapter.exportList>[0];

  const { mutate: handleExport, isLoading: isExporting } =
    useExportActivityLogs(exportActivityLogPayload);

  const isLoading =
    areActivityLogsLoading ||
    (Boolean(uniqueUserIdsArray.length) && isLoadingUsers) ||
    (Boolean(uniqueRoleIdsArray.length) && isLoadingRoles);

  const headers = useMemo<Array<HeaderType<ActivityType>>>(
    () => [
      {
        key: "user",
        label: "User",
        renderComponent: ({ item }) => {
          const userId =
            (item.before as UserRoleActivityLogBeforeAfter)?.user_id ??
            (item.after as UserRoleActivityLogBeforeAfter)?.user_id ??
            "-";
          const userName = userIdsToUserDetailsMap[userId]?.name;
          return userId && userName ? (
            <UserDataDisplay
              id={userId}
              name={userName}
              isLoading={isLoading}
            />
          ) : (
            "-"
          );
        },
      },
      {
        key: activityModelKeys.action,
        label: "Type",
        renderComponent: ({ item }) => {
          return item.action === ActivityAction.USER_ADD ? (
            <Pill
              variant="success"
              isLoading={isLoading}
            >
              Added to role
            </Pill>
          ) : item.action === ActivityAction.USER_REMOVE ? (
            <Pill
              variant="error"
              isLoading={isLoading}
            >
              Removed from role
            </Pill>
          ) : null;
        },
      },
      {
        key: activityModelKeys.resource_id,
        label: "Role",
        renderComponent: ({ item }) => {
          const roleId = item.resource_id;
          const roleName = roleIdsToRoleDetailsMap[roleId]?.name;
          return roleId && roleName ? (
            <RoutingLink to={linkToRoleDetailPage(roleId)}>
              {roleName}
            </RoutingLink>
          ) : (
            "-"
          );
        },
      },
      {
        key: activityModelKeys.author_id,
        label: "Logged By",
        renderComponent: ({ item }) => {
          const authorId = item.author_id;
          const authorName = userIdsToUserDetailsMap[authorId]?.name;
          return authorId && authorName ? (
            <UserDataDisplay
              id={authorId}
              name={authorName}
              isLoading={isLoading}
            />
          ) : (
            "-"
          );
        },
      },
      {
        key: activityModelKeys.timestamp,
        label: "Logged At",
        isSortable: true,
        renderComponent: ({ item }) => (
          <DataTable.DataRow.DateCell
            value={item[activityModelKeys.timestamp]}
            withTime
            isLoading={isLoading}
          />
        ),
      },
    ],
    [userIdsToUserDetailsMap, roleIdsToRoleDetailsMap]
  );

  const actionRow = [
    <PermissionAwareExportButton
      key="export"
      onClick={handleExport}
      isExporting={isExporting}
    />,
  ];

  return (
    <DataTablePanel
      panelProps={{
        title: "User Role Change Log",
        actionRow,
      }}
      dataTableProps={{
        headers,
        items: filteredActivities ?? [],
        isLoading,
        sorting: {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
        },
        pagination: {
          page: tableState.page,
          pageSize: tableState.pageSize,
          total: activitiesData?.total_entries,
          pageSizeText: "logs per page",
        },
        onSortChange: updateTableState,
        onPaginationChange: updateTableState,
        selected: selectedActivities,
        onSelectionChange: setSelectedActivities,
        getItemId: (item) => item.id,
        getItemBulkActions: ({ buttonProps }) => [
          canExportPermission ? (
            <Button
              {...buttonProps}
              key="export"
              variant="outline"
              slotLeft={<Icon variant="arrow-square-out" />}
              isLoading={isExporting}
              onClick={() => handleExport()}
            >
              Export
            </Button>
          ) : null,
        ],
      }}
    />
  );
};
